import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { fetchReservation, verifyReservationPayment, cancelReservation, payReservationDeposit } from "@/lib/api";
import { WhatsappPayFallback } from "@/components/WhatsappPayFallback";
import { fmtPrice , withWatermark } from "@/lib/utils";
import { YangoRideButton } from "@/components/YangoRideButton";

const pad = (n) => String(n).padStart(2, "0");

export default function HotelReservationStatusPage() {
  const { id, ref } = useParams();
  const [params, setParams] = useSearchParams();
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cancelPhone, setCancelPhone] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [paying, setPaying] = useState(false);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await payReservationDeposit(ref);
      if (r.payment_url) window.location.href = r.payment_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de generer le lien de paiement. Reessayez.");
    } finally { setPaying(false); }
  };

  const load = () => fetchReservation(ref).then(setRes).catch(() => setError("Reservation introuvable"));

  useEffect(() => {
    if (params.get("paiement")) {
      setBusy(true);
      verifyReservationPayment(ref).finally(() => { setBusy(false); load(); params.delete("paiement"); params.delete("tx"); params.delete("status"); params.delete("dryrun"); setParams(params, { replace: true }); });
    } else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const doCancel = async () => {
    setBusy(true);
    try {
      const r = await cancelReservation(ref, cancelPhone);
      toast.success(r.penalty_amount ? `Annulee. Penalite ${fmtPrice(r.penalty_amount)}, remboursement ${fmtPrice(r.refund_amount)}.` : "Reservation annulee.");
      setShowCancel(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Annulation impossible"); } finally { setBusy(false); }
  };

  if (error) return <main className="max-w-md mx-auto px-4 py-10 text-center font-bold text-red-600" data-testid="reservation-status-error">{error}</main>;
  if (!res) return <main className="max-w-md mx-auto px-4 py-10 text-center" data-testid="reservation-status-loading"><Loader2 className="animate-spin mx-auto" /></main>;

  const s = res.status;
  const tone = s === "confirmee" ? "emerald" : s === "en_attente_acompte" ? "amber" : "red";
  const Icon = s === "confirmee" ? CheckCircle2 : s === "en_attente_acompte" ? Clock : XCircle;
  const dayUse = res.offer_type === "day_use";
  const label = dayUse && s === "confirmee" ? "Reservation par heure confirmee — paiement sur place" : { confirmee: "Reservation confirmee — acompte paye", en_attente_acompte: "En attente de l'acompte", annulee: "Reservation annulee", expiree: "Reservation expiree (acompte non paye)" }[s] || s;

  return (
    <main className="max-w-md mx-auto px-4 py-6" data-testid="reservation-status-page">
      <Link to={`/hotelleries/${id}`} className="inline-flex items-center gap-1 text-orange-600 font-semibold mb-4 text-sm"><ChevronLeft size={14} /> Retour a l&apos;hotel</Link>
      <div className="glass-card overflow-hidden">
        {res.hotel?.image && <img src={withWatermark(res.hotel.image)} alt={res.hotel_name} className="w-full h-32 object-cover" />}
        <div className="p-5">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold bg-${tone}-50 text-${tone}-700`} data-testid="reservation-status-badge">
            <Icon size={16} /> {label}
          </div>
          <h1 className="font-display text-2xl font-extrabold mt-3">{res.hotel_name}</h1>
          <div className="mt-3 text-sm space-y-1">
            <div><b>Ref :</b> <span className="font-mono" data-testid="reservation-ref">{res.ref}</span></div>
            <div><b>Date :</b> {res.date} a {pad(res.hour)}:00 · {res.rooms} chambre(s)</div>
            {dayUse ? (
              <div data-testid="reservation-day-use"><b>Creneau :</b> {String(res.hour).padStart(2, "0")}h - {String(res.hour + (res.duree_heures || 1)).padStart(2, "0")}h ({res.duree_heures} h, -{res.reduction}%) · <b>A payer sur place :</b> {fmtPrice(res.total_price)} · Annulation gratuite avant le debut</div>
            ) : (
              <div><b>Total :</b> {fmtPrice(res.total_price)} · <b>Acompte :</b> <span data-testid="reservation-deposit">{fmtPrice(res.deposit_amount)}</span> {res.deposit_paid ? "✓ paye" : "(a payer)"}</div>
            )}
            {res.deposit_paid && <div><b>Solde a la reception :</b> {fmtPrice(res.total_price - res.deposit_amount)}</div>}
            {s === "en_attente_acompte" && res.hold_until && <div className="text-amber-700 text-xs">Chambre bloquee jusqu&apos;a {new Date(res.hold_until).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}.</div>}
            {s === "annulee" && res.deposit_paid && <div className="text-xs">Penalite {fmtPrice(res.penalty_amount || 0)} · Remboursement {fmtPrice(res.refund_amount || 0)} (sous 72 h)</div>}
          </div>

          {s === "en_attente_acompte" && (
            <button type="button" onClick={pay} disabled={paying} className="btn-primary w-full mt-5 inline-flex justify-center" data-testid="reservation-pay-btn">{paying ? "Génération du lien de paiement..." : `Payer l'acompte ${fmtPrice(res.deposit_amount)} (Mobile Money / carte)`}</button>
          )}
          {s === "en_attente_acompte" && (
            <WhatsappPayFallback plan="Acompte Hotel" amount={res.deposit_amount} hotelId={res.hotel_id} reservationRef={res.ref} />
          )}
          {s === "en_attente_acompte" && (
            <button onClick={() => { setBusy(true); verifyReservationPayment(ref).finally(() => { setBusy(false); load(); }); }} disabled={busy} className="btn-secondary w-full mt-2 text-sm" data-testid="reservation-verify-btn">
              {busy ? "Verification..." : "J'ai paye — verifier"}
            </button>
          )}
          {s === "confirmee" && <div className="mt-5"><YangoRideButton hotel={{ ...res.hotel, id: res.hotel_id }} /></div>}

          {(s === "confirmee" || s === "en_attente_acompte") && (
            <div className="mt-5 border-t border-[var(--line)] pt-4">
              {!showCancel ? (
                <button onClick={() => setShowCancel(true)} className="text-sm text-red-600 font-semibold underline" data-testid="reservation-cancel-open">Annuler la reservation</button>
              ) : (
                <div className="space-y-2" data-testid="reservation-cancel-panel">
                  {res.penalty_if_cancel_now > 0 && <div className="text-xs text-red-700 font-semibold" data-testid="reservation-cancel-penalty">Penalite appliquee : {fmtPrice(res.penalty_if_cancel_now)} (20 % de l&apos;acompte, annulation plus de 30 min apres paiement).</div>}
                  <input value={cancelPhone} onChange={(e) => setCancelPhone(e.target.value)} placeholder="Votre numero de telephone (confirmation)" data-testid="reservation-cancel-phone" />
                  <div className="flex gap-2">
                    <button onClick={doCancel} disabled={busy} className="btn-primary !bg-red-600 flex-1 text-sm" data-testid="reservation-cancel-confirm">Confirmer l&apos;annulation</button>
                    <button onClick={() => setShowCancel(false)} className="btn-secondary flex-1 text-sm" data-testid="reservation-cancel-back">Retour</button>
                  </div>
                </div>
              )}
            </div>
          )}
          <p className="mt-4 text-[11px] text-[var(--ink-soft)]">{res.policy?.text}</p>
        </div>
      </div>
    </main>
  );
}
